'use client';

import { useCallback } from 'react';
import type { ExternalNetworkDto } from '@terra/shared-contracts-external-network';
import {
  ExternalNetworkScanningSettings,
  toasterApi,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  useAssignExternalNetworkScanProfile,
  useExternalNetworkScanProfiles,
} from '../../../../hooks/api/useExternalNetworkScanProfiles';
import { useInternalAccessNetworks } from '../../../../hooks/api/useInternalAccessNetworks';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';
import { useOpenWebAppPage } from '../../../../hooks/useOpenWebAppPage';
import { WEB_APP_PATHS } from '../../../../utils/webAppPaths';
import { ExternalConnectorNetworkPicker } from './ExternalConnectorNetworkPicker';
import { usePersistExternalNetworkSettings } from './usePersistExternalNetworkSettings';

type ExternalScanningSettingsPanelProps = {
  surface: ExternalNetworkDto;
  surfaceId: string;
  isReadOnly: boolean;
  isScanActive: boolean;
};

export function ExternalScanningSettingsPanel({
  surface,
  surfaceId,
  isReadOnly,
  isScanActive,
}: ExternalScanningSettingsPanelProps) {
  const t = useTranslations('externalNetwork.settings');
  const openWebAppPage = useOpenWebAppPage();
  const { isFeatureFlagEnabled: isConnectorScanEnabled } = useFeatureFlags(
    'EXTERNAL_NETWORK_CONNECTOR_SCAN_ENABLED',
  );
  const { profiles, isLoading: isProfilesLoading } =
    useExternalNetworkScanProfiles();
  const assignScanProfile = useAssignExternalNetworkScanProfile();
  const { isUpdating, persist } = usePersistExternalNetworkSettings({
    surfaceId,
    isReadOnly,
  });
  const {
    networks: internalAccessNetworks,
    isLoading: isInternalAccessNetworksLoading,
  } = useInternalAccessNetworks({ enabled: isConnectorScanEnabled });

  const handleAssignScanProfile = useCallback(
    async (scanProfileId: string): Promise<boolean> => {
      if (isReadOnly) {
        return false;
      }

      try {
        await assignScanProfile.mutateAsync({ surfaceId, scanProfileId });
        toasterApi.success({ title: t('saveSuccess') });

        return true;
      } catch (error: unknown) {
        console.error(
          new Error('Failed to assign external network scan profile', {
            cause: error instanceof Error ? error : new Error(String(error)),
          }),
        );
        toasterApi.error({ title: t('saveError') });

        return false;
      }
    },
    [assignScanProfile, isReadOnly, surfaceId, t],
  );

  return (
    <ExternalNetworkScanningSettings
      surface={surface}
      isReadOnly={isReadOnly}
      isScanActive={isScanActive}
      profiles={profiles}
      isProfilesLoading={isProfilesLoading}
      isConnectorScanEnabled={isConnectorScanEnabled}
      connectorNetworks={internalAccessNetworks}
      isUpdating={isUpdating}
      isAssigningProfile={assignScanProfile.isPending}
      onPersist={persist}
      onAssignScanProfile={handleAssignScanProfile}
      onManageScanProfiles={() =>
        openWebAppPage(WEB_APP_PATHS.externalNetworkScanProfiles)
      }
      renderConnectorNetworkPicker={({
        internalAccessNetworkId,
        onInternalAccessNetworkIdChange,
      }) => (
        <ExternalConnectorNetworkPicker
          internalAccessNetworkId={internalAccessNetworkId}
          onInternalAccessNetworkIdChange={onInternalAccessNetworkIdChange}
          internalAccessNetworks={internalAccessNetworks}
          isInternalAccessNetworksLoading={isInternalAccessNetworksLoading}
          disabled={isReadOnly}
        />
      )}
    />
  );
}
